if (!customElements.get('collapsible-content')) {
  customElements.define(
    'collapsible-content',
    class CollapsibleContent extends HTMLElement {
      constructor() {
        super();

        this.classes = {
          collapsed: 'is-collapsed',
          expanded: 'is-expanded',
          disabled: 'is-disabled'
        };

        this.isAnimating = false;
        this.animationTimeout = null;
      }

      connectedCallback() {
        this.innerEl = this.querySelector('.collapsible-content__inner');
        this.toggleBtn = this.querySelector('.collapsible-content__toggle');
        this.toggleBtnText = this.toggleBtn?.querySelector('.btn__text');
        this.details = this.closest('details');

        if (!this.innerEl || !this.toggleBtn || !this.details) return;

        this.abortController = new AbortController();
        this.enableDesktop = this.dataset.enableDesktop === 'true';
        this.enableMobile = this.dataset.enableMobile === 'true';
        this.heightDesktop = parseInt(this.dataset.heightDesktop, 10) || 200;
        this.heightMobile = parseInt(this.dataset.heightMobile, 10) || 200;
        this.showText = this.dataset.showText;
        this.hideText = this.dataset.hideText;

        const { signal } = this.abortController;

        this.toggleBtn.addEventListener('click', this.onToggleClick.bind(this), { signal });
        this.details.addEventListener('toggle', this.onAccordionToggle.bind(this), { signal });
        this.innerEl.addEventListener('transitionend', this.onTransitionEnd.bind(this), { signal });
        document.addEventListener('matchMobile', this.onBreakpointChange.bind(this), { signal });
        document.addEventListener('unmatchMobile', this.onBreakpointChange.bind(this), { signal });

        this.innerEl.querySelectorAll('img').forEach((image) => {
          if (image.complete) return;

          image.addEventListener(
            'load',
            () => {
              if (this.isAccordionOpen && !this.classList.contains(this.classes.expanded)) {
                this.evaluate();
              }
            },
            { once: true, signal }
          );
        });

        if (this.details.hasAttribute('open')) {
          requestAnimationFrame(() => this.evaluate());
        } else {
          this.resetToCollapsed();
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.clearAnimationTimeout();
      }

      get isMobile() {
        if (window.FoxTheme?.config) {
          return FoxTheme.config.mqlMobile;
        }

        return window.matchMedia('screen and (max-width: 767px)').matches;
      }

      get isFeatureEnabled() {
        return this.isMobile ? this.enableMobile : this.enableDesktop;
      }

      get collapsedHeight() {
        return this.isMobile ? this.heightMobile : this.heightDesktop;
      }

      get isAccordionOpen() {
        return this.details.hasAttribute('open');
      }

      get prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      onAccordionToggle() {
        if (this.isAccordionOpen) {
          this.evaluate();
        } else {
          this.resetToCollapsed();
        }
      }

      onBreakpointChange() {
        if (!this.isAccordionOpen) {
          this.resetToCollapsed();
          return;
        }

        this.evaluate();
      }

      onToggleClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.classList.contains(this.classes.disabled) || this.isAnimating) return;

        if (this.classList.contains(this.classes.collapsed)) {
          this.expand();
        } else {
          this.collapse();
        }
      }

      onTransitionEnd(event) {
        if (event.target !== this.innerEl || event.propertyName !== 'max-height') return;
        this.finishAnimation();
      }

      evaluate() {
        if (!this.isFeatureEnabled) {
          this.disable();
          return;
        }

        if (this.innerEl.scrollHeight <= this.collapsedHeight) {
          this.disable();
          return;
        }

        this.enableToggle();
        this.collapse({ instant: true });
      }

      disable() {
        this.classList.add(this.classes.disabled);
        this.classList.remove(this.classes.collapsed, this.classes.expanded);
        this.innerEl.style.maxHeight = '';
        this.toggleBtn.hidden = true;
        this.toggleBtn.setAttribute('aria-expanded', 'false');
        this.isAnimating = false;
      }

      enableToggle() {
        this.classList.remove(this.classes.disabled);
        this.toggleBtn.hidden = false;
      }

      expand() {
        const startHeight = this.innerEl.offsetHeight;

        this.innerEl.style.maxHeight = `${startHeight}px`;
        this.classList.remove(this.classes.collapsed);
        this.classList.add(this.classes.expanded);
        this.updateButton(true);

        if (this.prefersReducedMotion) {
          this.innerEl.style.maxHeight = 'none';
          this.isAnimating = false;
          return;
        }

        this.startAnimation();

        requestAnimationFrame(() => {
          this.innerEl.style.maxHeight = `${this.innerEl.scrollHeight}px`;
        });
      }

      collapse({ instant = false } = {}) {
        const collapsedHeight = this.collapsedHeight;

        this.classList.add(this.classes.collapsed);
        this.classList.remove(this.classes.expanded);
        this.updateButton(false);

        if (instant || this.prefersReducedMotion) {
          this.innerEl.style.maxHeight = `${collapsedHeight}px`;
          this.isAnimating = false;
          this.clearAnimationTimeout();
          return;
        }

        const startHeight = this.innerEl.scrollHeight;
        this.innerEl.style.maxHeight = `${startHeight}px`;
        this.startAnimation();

        requestAnimationFrame(() => {
          this.innerEl.style.maxHeight = `${collapsedHeight}px`;
        });
      }

      resetToCollapsed() {
        this.clearAnimationTimeout();
        this.classList.add(this.classes.collapsed);
        this.classList.remove(this.classes.expanded, this.classes.disabled);
        this.innerEl.style.maxHeight = '';
        this.toggleBtn.hidden = true;
        this.updateButton(false);
        this.isAnimating = false;
      }

      updateButton(expanded) {
        this.toggleBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        if (this.toggleBtnText) {
          this.toggleBtnText.textContent = expanded ? this.hideText : this.showText;
        }
      }

      startAnimation() {
        this.isAnimating = true;
        this.clearAnimationTimeout();
        this.animationTimeout = setTimeout(() => this.finishAnimation(), 450);
      }

      finishAnimation() {
        this.clearAnimationTimeout();
        this.isAnimating = false;

        if (this.classList.contains(this.classes.expanded)) {
          this.innerEl.style.maxHeight = 'none';
        }
      }

      clearAnimationTimeout() {
        if (this.animationTimeout) {
          clearTimeout(this.animationTimeout);
          this.animationTimeout = null;
        }
      }
    }
  );
}
